import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Spinner,
  VisuallyHidden,
} from '@dfh-corp-sites/ui'
import { useLocation } from '@gatsbyjs/reach-router'
import React, { isValidElement, PropsWithChildren, useEffect } from 'react'

import useIsSsr from '../../../hooks/useIsSsr'
import { usePageUrl } from '../../../lib/pages'
import { ProductModalData, ProductModalState } from '../../../lib/products/modal/types'

export interface ProductModalProps<TData extends ProductModalData> {
  state: ProductModalState<TData>
  onCloseModal: () => void
  urlHash?: string
}

export const ProductModal = <TData extends ProductModalData>({
  children,
  state,
  onCloseModal,
  urlHash,
}: PropsWithChildren<ProductModalProps<TData>>) => {
  const isSsr = useIsSsr()
  const location = useLocation()
  const { navigate } = usePageUrl()

  // update the URL hash when the modal is opened/closed
  useEffect(() => {
    if (isSsr || !urlHash) {
      return
    }

    const hash = location.hash

    const urlWithoutHash = location.href.replace(location.origin, '').replace(hash, '')

    if (state.type === 'CLOSED') {
      navigate(urlWithoutHash)
      return
    }

    if (hash !== urlHash) {
      navigate(`${urlWithoutHash}${urlHash}`)
    }
  }, [location.href, location.hash, location.origin, navigate, isSsr, state, urlHash])

  useEffect(() => {
    const timeout = setTimeout(() => {
      const modal = document.getElementById('product-modal')
      if (modal) {
        modal.removeAttribute('tabindex')
      }
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  // close modal when back navigation is detected
  useEffect(() => {
    if (isSsr) {
      return
    }
    window.addEventListener('popstate', onCloseModal)
    return () => window.removeEventListener('popstate', onCloseModal)
  }, [isSsr, onCloseModal])

  if (state.type === 'CLOSED') {
    return null
  }
  let modalData: ProductModalData | undefined
  if (isValidElement(children) && children.props.modalData) {
    modalData = children.props.modalData
  }

  const productName = modalData?.item?.name || 'Product'
  const shortDescription = modalData?.productDetails?.shortDescription

  return (
    <Dialog defaultOpen onOpenChange={(isOpen) => !isOpen && onCloseModal()}>
      <DialogContent id="product-modal" className="overflow-y-visible">
        <VisuallyHidden>
          <DialogTitle>{productName}</DialogTitle>
          <DialogDescription>
            {shortDescription || 'More information about this product.'}
          </DialogDescription>
        </VisuallyHidden>

        <div>
          {state.type === 'OPEN' && children}
          {state.type === 'LOADING' && (
            <div className="flex justify-center items-center h-full min-h-52 p-5">
              <Spinner />
            </div>
          )}
          {state.type === 'ERROR' && (
            <div role="alert" className="flex justify-center items-center h-full p-5">
              <p>There was an error loading the product, please try again later.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
